/**
 * Public legal-document frame (privacy policy, terms of service).
 *
 * DRAFT SURFACE. The documents it renders are engineering drafts pending legal
 * review — see `src/content/legal.ts`. The review banner is part of the frame
 * rather than of one document's prose so that no document can be published
 * through here without it.
 *
 * Public and unauthenticated by construction: it renders content passed in and
 * makes no request of its own, so Google's crawler and a prospective student
 * reach it with no session and no tenant header. Tenant identity arrives through
 * `useTenantPresentation()` at the page, the same seam `/sign-in` and `/apply`
 * use, and the theme resolves from the host through the public token layer.
 */
import { Link } from "react-router";

import { BrandMark } from "@/components/BrandMark";
import { PUBLIC_LINK } from "@/components/public/control-classes";
import {
  LEGAL_DRAFT_NOTICE,
  type LegalBlock,
  type LegalDocument,
} from "@/content/legal";

interface LegalDocumentPageProps {
  brandName: string;
  logoUrl: string | null;
  parentOrg: string | null;
  content: LegalDocument;
  /** The sibling document, linked from the header and the foot of the page. */
  sibling: { label: string; to: string };
}

function Block({ block, index }: { block: LegalBlock; index: number }) {
  switch (block.kind) {
    case "paragraph":
      return (
        <p className="mt-4 text-body text-[var(--public-foreground-soft)] first:mt-0">
          {block.text}
        </p>
      );
    case "list":
      return (
        <ul className="mt-4 grid gap-2 first:mt-0">
          {block.items.map((item) => (
            <li
              key={item}
              className="relative pl-5 text-body text-[var(--public-foreground-soft)] before:absolute before:left-0 before:top-[0.72em] before:size-[5px] before:rounded-full before:bg-[var(--public-highlight)]"
            >
              {item}
            </li>
          ))}
        </ul>
      );
    case "definitions":
      return (
        <dl className="mt-4 divide-y divide-[var(--public-rule)] border-y border-[var(--public-rule)] first:mt-0">
          {block.items.map((item) => (
            <div key={item.term} className="py-4 sm:grid sm:grid-cols-[13rem_minmax(0,1fr)] sm:gap-6">
              <dt className="font-mono text-[10.5px] font-semibold uppercase leading-[1.6] tracking-[0.16em] text-[var(--public-primary-deep)]">
                {item.term}
              </dt>
              <dd className="mt-1.5 text-body text-[var(--public-foreground-soft)] sm:mt-0">
                {item.description}
              </dd>
            </div>
          ))}
        </dl>
      );
    case "pending":
      return (
        <p
          data-legal-pending
          aria-label={`Awaiting owner input, item ${index + 1}`}
          className="mt-4 border-l-2 border-[var(--public-readable-accent)] bg-[var(--public-surface)] px-4 py-3 font-mono text-meta text-[var(--public-readable-accent)] first:mt-0"
        >
          {block.text}
        </p>
      );
  }
}

export function LegalDocumentPage({
  brandName,
  logoUrl,
  parentOrg,
  content,
  sibling,
}: LegalDocumentPageProps) {
  return (
    <div className="min-h-screen bg-[var(--public-background)] text-[var(--public-foreground)]">
      <header className="border-b border-[var(--public-rule)] bg-[var(--public-background-deep)] px-5 py-4 sm:px-10">
        <div className="mx-auto flex w-full max-w-[880px] flex-wrap items-center justify-between gap-x-6 gap-y-3">
          <Link to="/" className={`${PUBLIC_LINK} flex items-center gap-3`} aria-label={`${brandName} home`}>
            <BrandMark
              name={brandName}
              logoUrl={logoUrl}
              className="size-9 shrink-0 text-[11px] text-[var(--public-primary)]"
            />
            <span className="font-display text-[18px] font-semibold leading-none tracking-[0.01em] text-[var(--public-foreground)]">
              {brandName}
              {parentOrg && (
                <small className="mt-[5px] block font-mono text-[8.5px] font-medium uppercase tracking-[0.28em] text-[var(--public-foreground-muted)]">
                  {parentOrg}
                </small>
              )}
            </span>
          </Link>
          <Link
            to="/sign-in"
            className={`${PUBLIC_LINK} font-mono text-[9.5px] font-semibold uppercase tracking-[0.2em] text-[var(--public-primary-deep)] underline-offset-4 transition-colors hover:text-[var(--public-primary)] hover:underline`}
          >
            Sign in
          </Link>
        </div>
      </header>

      <main className="px-5 py-12 sm:px-10 md:py-16">
        <article className="mx-auto w-full max-w-[880px]">
          <p className="font-mono text-[9.5px] font-semibold uppercase tracking-[0.22em] text-[var(--public-highlight-deep)]">
            {brandName} · Legal
          </p>
          <h1 className="mt-3.5 font-display text-[38px] font-semibold leading-[1.02] tracking-[-0.03em] text-[var(--public-foreground)] md:text-[46px]">
            {content.title}
          </h1>
          <p className="mt-4 max-w-[60ch] font-display text-[19px] leading-[1.5] text-[var(--public-foreground-soft)]">
            {content.lede}
          </p>

          {/* Unmissable by design: the first thing under the title, in the
              accent reserved for unresolved items, and never optional. */}
          <aside
            role="note"
            data-legal-draft-banner
            aria-labelledby="legal-draft-banner-label"
            className="mt-8 border border-[var(--public-readable-accent)] bg-[var(--public-surface)] px-5 py-5 sm:px-6"
          >
            <p
              id="legal-draft-banner-label"
              className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.2em] text-[var(--public-readable-accent)]"
            >
              {LEGAL_DRAFT_NOTICE.label}
            </p>
            <p className="mt-3 text-body text-[var(--public-foreground)]">
              {LEGAL_DRAFT_NOTICE.body}
            </p>
          </aside>

          <nav aria-label="Contents" className="mt-10 border-t border-[var(--public-rule)] pt-6">
            <ol className="flex flex-wrap gap-x-6 gap-y-2">
              {content.sections.map((section, index) => (
                <li key={section.id}>
                  <a
                    href={`#${section.id}`}
                    className={`${PUBLIC_LINK} font-mono text-[10px] uppercase tracking-[0.14em] text-[var(--public-foreground-muted)] underline-offset-4 transition-colors hover:text-[var(--public-primary)] hover:underline`}
                  >
                    <span aria-hidden="true">{String(index + 1).padStart(2, "0")} </span>
                    {section.heading}
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          {content.sections.map((section) => (
            <section
              key={section.id}
              id={section.id}
              aria-labelledby={`${section.id}-heading`}
              className="mt-12 scroll-mt-8 border-t border-[var(--public-rule)] pt-8"
            >
              <h2
                id={`${section.id}-heading`}
                className="font-display text-[25px] font-semibold leading-[1.15] tracking-[-0.02em] text-[var(--public-foreground)]"
              >
                {section.heading}
              </h2>
              <div className="mt-5">
                {section.blocks.map((block, index) => (
                  <Block key={`${section.id}-${index}`} block={block} index={index} />
                ))}
              </div>
            </section>
          ))}
        </article>
      </main>

      <footer className="border-t border-[var(--public-rule)] bg-[var(--public-background-deep)] px-5 py-6 sm:px-10">
        <div className="mx-auto flex w-full max-w-[880px] flex-col gap-3 text-small text-[var(--public-foreground-muted)] sm:flex-row sm:items-center sm:justify-between">
          <p>
            <span className="font-display text-[15px] font-semibold text-[var(--public-foreground)]">
              {brandName}
            </span>
            {parentOrg ? ` · ${parentOrg}` : ""}
          </p>
          <Link
            to={sibling.to}
            className={`${PUBLIC_LINK} font-mono text-[9.5px] font-semibold uppercase tracking-[0.18em] text-[var(--public-primary-deep)] underline-offset-4 transition-colors hover:text-[var(--public-primary)] hover:underline`}
          >
            {sibling.label}
          </Link>
        </div>
      </footer>
    </div>
  );
}
